import {
  ButtonV3 as Button,
  type RadialGradientBGProps,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';

import ImageWrapper from '../LandingPageFeatures/ImageWrapper';
import type { LandingPageCardData } from './types';

export interface LandingPageCardProps extends LandingPageCardData {
  readonly cardsCount: number;
  readonly index: number;
  readonly imageGradientBg?: RadialGradientBGProps | undefined;
}

const getImageSizes = (cardsCount: number): string => {
  const sizes = ['(max-width: 767px) 90vw', '(max-width: 1023px) 60vw'];

  if (cardsCount === 2) {
    sizes.push('(max-width: 1567px) 45vw');
    sizes.push('704px');
  }

  if (cardsCount === 3) {
    sizes.push('(max-width: 1567px) 30vw');
    sizes.push('459px');
  }

  if (cardsCount >= 4) {
    sizes.push('(max-width: 1567px) 22vw');
    sizes.push('336px');
  }

  return sizes.join(', ');
};

const LandingPageCard = ({
  cardsCount,
  link,
  description,
  image,
  imageAspectRatio = '1:1',
  imageGradientBg,
  index,
  title,
}: LandingPageCardProps) => {
  const t = useTranslations();

  return (
    <Motion
      className="size-full"
      initial={{ opacity: 0 }}
      transition={{ delay: 0.1 * index, duration: 0.75, type: 'tween' }}
      viewport={{ amount: 0.66, once: true }}
      whileInView={{ opacity: 1 }}
    >
      <div
        className="flex h-full flex-col items-start"
        data-cy="landing-page-card"
      >
        <div
          className={cx(
            'relative mb-6 w-full overflow-hidden rounded-lg lg:mb-8',
            {
              'aspect-square': imageAspectRatio === '1:1',
              'aspect-4/3': imageAspectRatio === '4:3',
            },
          )}
          data-cy="landing-page-card-image-wrapper"
        >
          <ImageWrapper imageGradientBg={imageGradientBg}>
            <Image
              {...image}
              className="size-full object-cover object-center"
              data-cy="landing-page-card-image"
              fill
              sizes={getImageSizes(cardsCount)}
            />
          </ImageWrapper>
        </div>
        <TypographyV3
          className="mb-2 leading-snug text-inherit"
          Element="h2"
          variant="h5"
        >
          {t(title)}
        </TypographyV3>
        <TypographyV3 className="mb-auto text-inherit">
          <RichText messageKey={description} />
        </TypographyV3>
        {link && (
          <Button
            className="mt-6 text-inherit"
            data-cy={link.dataCy ?? 'landing-page-card-link'}
            href={link.href}
            onClick={link.onClick}
            size="none"
            target={link.target}
            variant="primary-text-link"
          >
            <span className="underline">
              <RichText messageKey={link.label} tValues={link.values} />
            </span>
          </Button>
        )}
      </div>
    </Motion>
  );
};

export default LandingPageCard;
